import { createSlice } from '@reduxjs/toolkit';

import pushToGTM, { usedFilterTagGA4 } from '../../utils/analytics/gtm';
import { fetchByCategoryParam } from './categoryDataSlice';
import { DATA_TYPES } from '../../components/Filter/constants';

function assignInitialFilterValue(dataType, constraints) {
	switch (dataType) {
		case DATA_TYPES.BOOLEAN:
			return null;
		case DATA_TYPES.NUMERIC:
			const minValue = constraints && constraints.minValue;
			const maxValue = constraints && constraints.maxValue;

			if (constraints.minOperator && constraints.maxOperator) {
				// range slider
				return [minValue, maxValue];
			} else if (constraints.minOperator || constraints.maxOperator) {
				// single slider
				return constraints.minOperator ? minValue : maxValue;
			} else {
				// input
				return '';
			}
		case DATA_TYPES.STRING:
			return [];
		default:
			return null;
	}
}

const filtersSlice = createSlice({
	name: 'filters',
	initialState: {
		appliedState: {},
		latestFilterChange: {},
		filters: [],
		similarParts: {
			opn: '',
			wpn: '',
			autoSet: false,
			parts: []
		},
		countOfPartsForFilterOptions: {},
		filtersOptions: {}
	},
	reducers: {
		resetAllFilters: state => {
			const newState = JSON.parse(JSON.stringify(state.appliedState));
			for (const cid in newState) {
				if (Object.prototype.hasOwnProperty.call(newState, cid)) {
					newState[cid].logic = newState[cid].defaultLogic;
					newState[cid].value = newState[cid].defaultValue;
					newState[cid].disabled = false;
					newState[cid].used = false;
					newState[cid].isDirty = false;
				}
			}

			state.appliedState = newState;
			state.similarParts.wpn = '';
			state.similarParts.opn = '';
			state.similarParts.parts = [];
		},
		resetSimilarPart: state => {
			state.similarParts.wpn = '';
			state.similarParts.opn = '';
			state.similarParts.parts = [];
			state.similarParts.autoSet = false;
		},
		resetSimilarPartFiltersAutoSet: state => {
			state.similarParts.autoSet = false;
		},
		updatePartCountForFilters: (state, action) => {
			state.countOfPartsForFilterOptions = action.payload;
		},
		updateFilterAppliedLogic: (state, action) => {
			const { cid, value } = action.payload;
			state.appliedState[cid].logic = value;
		},
		restoreAppliedFilterState: (state, action) => {
			Object.entries(action.payload).forEach(([cid, savedFilter]) => {
				if (!state.appliedState[cid]) return;

				state.appliedState[cid] = {
					...state.appliedState[cid],
					logic: savedFilter.logic,
					value: savedFilter.value,
					disabled: savedFilter.disabled,
					used: savedFilter.used,
					isDirty: savedFilter.isDirty
				};
			});
		},
		updateSimilarParts: (state, action) => {
			let { wpn, opn, similarParts, filterAutoset } = action.payload;
			state.similarParts.wpn = wpn;
			state.similarParts.opn = opn;
			state.similarParts.parts = similarParts;
			state.similarParts.autoSet = filterAutoset;
		},

		updateFilterAppliedValue: (state, action) => {
			const _ = require('lodash');
			let { cid, value } = action.payload;

			if (!_.isEqual(state.appliedState[cid].value, value)) {
				pushToGTM(usedFilterTagGA4(state.filters, cid));
			}

			if (!state.filters.find(filter => filter.cid === cid).multiSelect && Array.isArray(value) && value.length === 1) {
				value = value[0];
			}

			// record latest filter change for ranking purposes if necessary
			try {
				state.latestFilterChange = {
					cid: cid,
					type: state.filters.find(f => f.cid === cid).type,
					value: value,
					previousValue: state.appliedState[cid].value,
					multiSelect: state.filters.find(f => f.cid === cid).multiSelect
				};
			} catch (e) {
				console.error('Error updating latest filter change:', e);
			}

			state.appliedState[cid].value = value;
			state.appliedState[cid].used =
				value !== state.appliedState[cid].defaultValue &&
				JSON.stringify(value) !== JSON.stringify(state.appliedState[cid].defaultValue);
			state.appliedState[cid].isDirty = true;
			// disable filters according to "disabled" conditions
			try {
				state.filters.forEach(filter => {
					const disabledConditions = filter.disabled;

					if (disabledConditions !== null) {
						disabledConditions
							.filter(condition => condition.cid === cid)
							.forEach(relatedContidion => {
								// if value occures in array of conditions OR there is intersection between those two
								const conditionIntersection =
									(Array.isArray(value) && value.some(val => relatedContidion.values.includes(val))) ||
									relatedContidion.values.includes(value);
								// if value exactly matches conditions
								const conditionEquals = value === relatedContidion.values[0];

								state.appliedState[filter.cid].disabled = conditionIntersection || conditionEquals;
							});
					}
				});
			} catch (e) {
				console.error('Error checking disable conditions: ', e);
			}
		}
	},
	extraReducers: {
		[fetchByCategoryParam.fulfilled]: (_state, action) => {
			try {
				const { filters } = action.payload;
				const initialState = {};
				const defaultCountOfPartsForFiltersOptions = {};
				const filtersOptions = {};

				filters.forEach(filter => {
					const filterState = {
						defaultLogic: '',
						defaultValue: '',
						logic: '',
						value: '',
						disabled: false,
						used: false,
						isDirty: false
					};
					const filterOptionsForPartCount = {};
					const filterOptions = [];

					if (filter.conditional === true) {
						// if conditional filter contains 'All' value, we want to make it selected by default
						if (filter.values.some(value => value.label === 'All')) {
							filterState.defaultValue = 'All';
							filterState.value = 'All';
						} else {
							filterState.defaultValue = [];
							filterState.value = [];
						}
						if (filter.type === 'S' || filter.type === 'B' || filter.type === 'N') {
							filter.values.forEach(value => {
								if (value.label !== 'All') {
									filterOptionsForPartCount[value.label] = {
										valuesCid: value.conditions[0].cid,
										values: value.conditions[0].values,
										count: 0
									};
								}
								filterOptions.push(value.label);
							});
						}
					} else {
						const filterValue = assignInitialFilterValue(filter.type, filter.constraints);
						filterState.defaultValue = filterValue;
						filterState.value = filterValue;
						if (filter.type === 'S' || filter.type === 'B') {
							filter.values.forEach(value => {
								filterOptionsForPartCount[value] = {
									valuesCid: filter.cid,
									values: [value],
									count: 0
								};
								filterOptions.push(value);
							});
						}
					}
					filterState.defaultLogic = filter.logic;
					filterState.logic = filter.logic;

					initialState[filter.cid] = filterState;

					if (filter.type === 'S' || filter.type === 'B') {
						defaultCountOfPartsForFiltersOptions[filter.cid] = filterOptionsForPartCount;
					}
					if (filter.type === 'S' || filter.type === 'N') {
						filtersOptions[filter.cid] = filterOptions;
					}
				});

				return {
					appliedState: initialState,
					latestFilterChange: {},
					filters: filters,
					similarParts: {
						wpn: '',
						opn: '',
						parts: []
					},
					countOfPartsForFilterOptions: defaultCountOfPartsForFiltersOptions,
					filtersOptions: filtersOptions
				};
			} catch (e) {
				console.error(e);
			}
		}
	}
});

export const {
	resetAllFilters,
	resetSimilarPart,
	resetSimilarPartFiltersAutoSet,
	updateFilterAppliedValue,
	updateFilterAppliedLogic,
	restoreAppliedFilterState,
	updateSimilarParts,
	updatePartCountForFilters
} = filtersSlice.actions;
export default filtersSlice.reducer;
